import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The AAAA DNS record type was created to hold IPv6 addresses. AAAA records, pronounced “quad A records” are similar to A records. They hold a 128-bit IPv6 address instead of a 32-bit IPv4 address.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("IPv6 did not exist when the original DNS specifications ("),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1035",
    rel: "noopener noreferrer"
  }, "1035"),
  /*#__PURE__*/_createTextVNode(") were written. Only a few years later, IPv6 was created to address the problem of IPv4 address space exhaustion. The AAAA record type was introduced in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1886",
    rel: "noopener noreferrer"
  }, "RFC 1886"),
  /*#__PURE__*/_createTextVNode(" in 1995. "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc3596",
    rel: "noopener noreferrer"
  }, "RFC 3596"),
  /*#__PURE__*/_createTextVNode(" is the current specification for AAAA records in the DNS.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-record-types/aaaa/the-dns-aaaa-record-type.jpg",
    alt: "Mapping names into addresses",
    title: "Mapping names into addresses",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Mapping names into addresses. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "AAAA record format", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Each AAAA record contains a single IPv6 address. Like all other records in the DNS, every AAAA record also has a Time-to-Live (TTL) value in seconds. Here is an example of a AAAA record in zone file format:", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("pre", null, "www.example.org. 600 AAAA 2001:0dc8:86a4:0000:0000:7a2f:2360:2341", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, "In a DNS message travelling across the Internet, a AAAA record contains an IPv6 address stored as 16 bytes of binary data.", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "In zone files, web portals, and other human-friendly applications, the IPv6 address for a AAAA record is represented in string format shown above. This format consists of eight groups of four hexadecimal digits. Each group is separated by a colon and represents 16 bits of the address.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h3", null, "IPv6 address shortcuts", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, "IPv6 addresses are long! Many IPv6 addresses contain groups of consecutive zero digits next to one another. As a shorthand notation, any number of consecutive groups of zeros can be replaced with two colons. Leading zeros in each group can also be omitted.", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "So for example the IPv6 loopback address can be legally written as \"0000:0000:0000:0000:0000:0000:0000:0001\" or \"0:0:0:0:0:0:0:1\" or simply as \"::1\". These representations are all equivalent. Every IPv6 address is always 128 bits long when sent over the Internet or used by an application.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "The A record type stores 32-bit IPv4 addresses. Type A records have been part of the DNS since the very beginning. IPv6 was not created until several years later. Type A records can only hold 32 bits of address, but IPv6 addresses are 128 bits long. So a new record type had to be created for IPv6 addresses: AAAA.")
], -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, "IPv4 addresses are 32 bits long which means there are roughly 4 billion possible IPv4 addresses. In the early days of the Internet when computers were large and few in number, this seemed like more than enough. But it wasn't long before the IPv4 address space began to feel small.", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "The growth of the Internet was explosive and global. Large organizations snapped up huge ranges, or subnets, of IPv4 addresses. Computers became far more numerous. Phones, smart appliances, and many other new types of devices drove even more demand for addresses. Suddenly, 4 billion possible IPv4 addresses wasn't nearly enough to sustain the growth of the Internet.", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "This led to the creation of IPv6. With 128 bit addresses, the IPv6 address space is massive compared to the IPv4 address space. There are approximately 340 trillion trillion trillion IPv6 addresses. This is 100 times the number of atoms on the surface of the Earth!", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h2", null, "Multiple AAAA records at a name", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, "More than one IPv6 address at a name in the DNS is typical for redundancy. This is accomplished by creating multiple AAAA records at a DNS name.", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "There will usually be type A records at the name as well since there are few Internet resources that only support IPv6.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Here is an example showing two type A records and two type AAAA records for "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" with a TTL value of ten minutes:")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("pre", null, "www.example.org. 600 A 10.0.0.1\nwww.example.org. 600 A 10.0.0.2\nwww.example.org. 600 AAAA 2001::1000:1\nwww.example.org. 600 AAAA 2001::1000:2", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, "The TTL value for all AAAA records at a single name must be identical. This is true for all the records of any type at a name.", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("h2", null, "Simultaneous queries for A and AAAA records", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", null, "Clients today often perform simultaneous queries for type A and type AAAA when they are about to connect to an endpoint by name. Unfortunately, the DNS does not allow a client to retrieve both A and AAAA records with a single query. Efforts have been made to streamline this, but to date none have been adopted.", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "A client capable of using both IPv4 and IPv6 will usually perform two separate DNS queries for the endpoint name. One query will be of type A and the other of type AAAA. All the addresses will be sorted into a list.", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, "The order will depend on the client implementation, local configuration, and policy. Some clients may prefer the IPv4 addresses in the type A response. Other clients may prefer the IPv6 addresses in the type AAAA response.", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("h2", null, "What TTL should you use for AAAA records?", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "You should use a TTL of one hour or more for AAAA records that are relatively static. Records that change frequently can have a TTL as low as a few minutes.")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "Shorter TTLs come with trade-offs. Use them with caution. TTLs under five minutes should be used sparingly. As a general rule of thumb, use shorter TTLs for DNS records that change frequently and longer TTLs for DNS records that are more static.", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("h2", null, "Load balancing for IPv6 endpoints", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Load balancing for AAAA records is effectively the same as for A records. However, there is an interesting consideration that network architects must consider once AAAA records are added for an endpoint: "),
  /*#__PURE__*/_createElementVNode("strong", null, "It is not possible for the DNS to force clients to use or prefer IPv4 or IPv6.")
], -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("h2", null, "DNS cannot enforce preference for IPv4 versus IPv6", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("p", null, "If both A and AAAA records exist for a particular name in the DNS, a client is free to connect to any address for that name. The DNS doesn't have any way to dictate which address should be preferred, or even which address family (IPv4 or IPv6) should be preferred. This can create interesting problems for network architects.", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "For example, a North American company adds IPv6 addresses for their website. They notice that few of their customers use IPv6 so to reduce costs, they do not allocate very many servers for IPv6. Fast-forward a year or two. The company expands into Asia. The use of IPv6 is more common in Asia. Suddenly, the company's servers experience many more IPv6 requests than in the past. Since there are few IPv6 servers deployed, customers in Asia may have difficulty reaching the company's website. The company may need to deploy more IPv6 servers to mitigate.", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("h2", null, "Domain Name System-based Blocklist (DNSBL)", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "DNSBLs use the DNS as a distributed database to store blocklists used to defeat email spam. DNSBLs can flag both IPv4 and IPv6 addresses as sources of spam.")
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, "A mail server can perform a DNS query with a specific format against a DNSBL to check if a sending host's IP address has been flagged as a source of spam. In this case, the mail server might reject or throttle requests from that sender's IP address.", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("p", null, "For IPv6 addresses, the DNSBL contains a record at a DNS name composed of the IPv6 address in reverse order, with each hex digit in the address as a label. This is like the format for IPv4 addresses, only much longer. The DNSBL contains a record of type A, not type AAAA, at this name if the address has been flagged as a source of spam. This may seem confusing, but DNSBLs only use the A records as markers.", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, "A mail server would follow these steps to use a DNSBL to check if an IPv6 address is a known source of spam:", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Construct a DNSBL lookup name by reversing the nibbles in the sender's IPv6 address and appending the DNSBL's domain name. For example, if the sender's IPv6 address is 2001:db8:1:2:3:4:567:89ab the lookup name for the example.org DNSBL would be "),
    /*#__PURE__*/_createElementVNode("code", null, "b.a.9.8.7.6.5.0.4.0.0.0.3.0.0.0.2.0.0.0.1.0.0.0.8.b.d.0.1.0.0.2.dnsbl.example.org.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, "Perform a standard DNS lookup with query type A (not type AAAA) for this lookup name."),
  /*#__PURE__*/_createElementVNode("li", null, "If the result is NXDOMAIN, there is no entry for the sender's IP in the blocklist. This means that the sender's IP is not flagged as a source of spam. Mail should be sent normally."),
  /*#__PURE__*/_createElementVNode("li", null, "A positive result indicates that the sender's IP exists in the blocklist. This means the IP is considered a source of spam. A TXT lookup for the same name can be performed to retrieve the reason for blocklisting.")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The address returned for a blocked IP address may be the fixed value 127.0.0.2. This indicates that the sender's IP address has been flagged as a source of spam. Other values are also possible. See "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc5782#section-2.3",
    rel: "noopener noreferrer"
  }, "RFC 5782 section 2.3"),
  /*#__PURE__*/_createTextVNode(" for more details.")
], -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("h2", null, "Looking up AAAA records for a name", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "The IPv6 address records for a DNS name can be queried with the dig or nslookup command line tools. To find the address records for wikipedia.org, use this command:", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("pre", null, "dig AAAA wikipedia.org", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=aaaa wikipedia.org", -1)
const _hoisted_46 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_47 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_48 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_49 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_50 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What is the difference between a CNAME record and a AAAA record? ", -1)
const _hoisted_51 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_52 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "A CNAME is an alias from one DNS name to another. A CNAME is often used to redirect one DNS name to another DNS name. The target name will generally resolve to a set of A records and often also a set of AAAA records.")
], -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, "One use of CNAMEs is to eliminate duplicate data from the DNS. This reduces the chance of an administrator forgetting to update all copies of duplicated address records when making a change. CNAMEs may also be linked together in chains. The last name in the CNAME chain should usually resolve to a set of A and/or AAAA records.", -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Does a domain need a AAAA record? ", -1)
const _hoisted_55 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_56 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, [
    /*#__PURE__*/_createTextVNode("A domain such as "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(" does not necessarily need either an A or AAAA record. However, most domains have an A record and quite a few today also have AAAA records for clients who may want to use IPv6.")
  ])
], -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("It is typical for web clients to interchangeably connect to both "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(". If there are no A or AAAA records for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" then web browsers would display an error if a user did not explicitly type "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(". This would be confusing for clients of the domain.")
], -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import DnsRecordTypes from '@/views/learning/DnsRecordTypes.vue';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

const meta: BlogMeta = {
  tag: 'DNS record types',
  url: '/learning/dns-record-types/aaaa/',
  breadcrumbs: [learningBreadcrumb, DnsRecordTypes.breadcrumb],
  title: 'The AAAA record',
  description: 'The AAAA DNS record type was created to hold IPv6 addresses. AAAA records, pronounced “quad A records” are similar to A records but hold a 128-bit IPv6 address.',
  overviewDescription: 'The AAAA DNS record type was created to hold IPv6 addresses. AAAA records, pronounced “quad A records” are similar to A records, but hold a 128-bit IPv6 address instead of a 32-bit IPv4 address.',
  overviewImage: '/img/learning/dns-record-types/aaaa/the-dns-aaaa-record-type-small.jpg',
  overviewImageAlt: 'Mapping names into numbers',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-record-types/aaaa/the-aaaa-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-record-types/aaaa/the-aaaa-record-social.jpg',
  written: new Date('2022-07-24T14:48:23.618Z'),
  updated: new Date('2023-03-28T11:57:13.055Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecordTypeAAAA',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/aaaa/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dns-record-types/aaaa/the-dns-aaaa-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_6
          ]),
          _: 1
        }),
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _createVNode(Callout, {
          icon: "info",
          title: "AAAA versus A",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_12,
            _hoisted_13,
            _hoisted_14,
            _hoisted_15
          ]),
          _: 1
        }),
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_20
          ]),
          _: 1
        }),
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _createElementVNode("p", null, [
          _createTextVNode("TTL selection in the DNS is often complicated. As we discussed in "),
          _createVNode(_component_router_link, { to: "/learning/what-is-a-good-ttl-for-dns/" }, {
            default: _withCtx(() => [
              _createTextVNode("our article on TTL selection in the DNS")
            ]),
            _: 1
          }),
          _createTextVNode(", shorter TTLs give agility, but longer TTLs increase reliability. Longer TTLs can also decrease latency.")
        ]),
        _hoisted_28,
        _hoisted_29,
        _createElementVNode("p", null, [
          _createTextVNode("As we discussed in our article on the "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
            default: _withCtx(() => [
              _createTextVNode("A record type")
            ]),
            _: 1
          }),
          _createTextVNode(", there are different ways to perform load balancing for address records in the DNS. Any method used for IPv4 addresses can also be used for IPv6 addresses. Typically, this includes Anycast addresses or variations on round-robin.")
        ]),
        _hoisted_30,
        _hoisted_31,
        _hoisted_32,
        _hoisted_33,
        _hoisted_34,
        _hoisted_35,
        _hoisted_36,
        _hoisted_37,
        _hoisted_38,
        _hoisted_39,
        _hoisted_40,
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _hoisted_44,
        _hoisted_45,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the AAAA records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/aaaa-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("AAAA lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool, or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_46, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find AAAA record",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_47, [
          _hoisted_48,
          _createElementVNode("div", _hoisted_49, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_50,
                    _createElementVNode("span", _hoisted_51, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_52,
                    _hoisted_53,
                    _createElementVNode("p", null, [
                      _createTextVNode("See our article on "),
                      _createVNode(_component_router_link, { to: "/learning/dns-record-types/cname/" }, {
                        default: _withCtx(() => [
                          _createTextVNode("CNAME records")
                        ]),
                        _: 1
                      }),
                      _createTextVNode(" for more details.")
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_54,
                    _createElementVNode("span", _hoisted_55, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_56,
                    _hoisted_57
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})